import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, X } from 'lucide-react';
import { useUserCommunities } from '@/hooks/useUserCommunities';

interface JoinedCommunitiesProps {
  profileId: string;
  isOwner?: boolean;
}

export const JoinedCommunities = ({ profileId, isOwner = false }: JoinedCommunitiesProps) => {
  const { userCommunities, leaveCommunity, loading } = useUserCommunities(profileId);

  const handleLeaveCommunity = async (communityId: string) => {
    if (window.confirm('Are you sure you want to leave this community?')) {
      await leaveCommunity(communityId);
    }
  };

  if (loading) {
    return (
      <Card className="border-none bg-white/80 backdrop-blur-sm">
        <CardContent className="p-2">
          <div className="flex items-center gap-1 mb-1">
            <Users className="h-3 w-3 text-primary" />
            <span className="font-medium text-xs">Communities</span>
          </div>
          <div className="flex gap-1">
            {[1, 2].map(i => (
              <div key={i} className="h-5 bg-muted rounded animate-pulse w-16"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (userCommunities.length === 0) {
    return (
      <Card className="border-none bg-white/80 backdrop-blur-sm">
        <CardContent className="p-2">
          <div className="flex items-center gap-1 mb-1">
            <Users className="h-3 w-3 text-primary" />
            <span className="font-medium text-xs">Communities</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {isOwner ? "No communities joined" : "Not in any communities"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none bg-white/80 backdrop-blur-sm">
      <CardContent className="p-2">
        <div className="flex items-center gap-1 mb-1">
          <Users className="h-3 w-3 text-primary" />
          <span className="font-medium text-xs">Communities ({userCommunities.length})</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {userCommunities.map((userCommunity) => (
            <div key={userCommunity.id} className="group relative">
              <Badge
                variant="secondary"
                className="text-xs py-0.5 px-1.5 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors h-5"
              >
                {userCommunity.community.name}
                {isOwner && (
                  <Button
                    onClick={() => handleLeaveCommunity(userCommunity.community_id)}
                    variant="ghost"
                    size="sm"
                    className="absolute -right-0.5 -top-0.5 h-3 w-3 p-0 hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                  >
                    <X className="h-1.5 w-1.5 text-destructive" />
                  </Button>
                )}
              </Badge>
            </div>
          ))}
        </div>
        {isOwner && userCommunities.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1 leading-tight">
            Hover and click × to leave
          </p>
        )}
      </CardContent>
    </Card>
  );
};